import type { Address, MessageHeader, TaskRemind } from '@/api/types';
import { addDays, formatDueTime } from '@/features/tasks/due';
import { dateLocale, displayName, formatFullDate, nowWallTime, todayCalendarDay } from '@/utils/format';

/**
 * "Add to Tasks" / "Remind me about this email" (ADR 0075): the pure parts.
 *
 * A task made from an email carries a link back to it (where it is, and its
 * Message-ID) and one plain line of notes, "From <sender> · <date>". Nothing
 * else of the email goes into the task: its subject becomes the title, and
 * the body stays in the mailbox.
 */

/** One email to make a task from: a list row or the open message. */
export interface EmailTaskTarget {
  /** The ROW's mailbox (the unified view mixes them): the task goes there. */
  accountId: string;
  /** The app id of the message. */
  messageId: string;
  subject: string;
  from: Address;
  date: string;
  /** Known when the message is open (its headers); otherwise the server reads it. */
  rfcMessageId?: string | undefined;
}

/** The Message-ID from a message's headers, without angle brackets; undefined when it has none. */
export function rfcMessageIdOf(headers: readonly MessageHeader[] | undefined): string | undefined {
  const header = headers?.find((entry) => entry.name.toLowerCase() === 'message-id');
  const value = header?.value.trim().replace(/^<|>$/g, '').trim();
  return value === undefined || value === '' || /\s/.test(value) ? undefined : value;
}

/** The title a task from this email starts with: its subject, or who sent it. */
export function emailTaskTitle(target: Pick<EmailTaskTarget, 'subject' | 'from'>): string {
  const subject = target.subject.trim();
  return subject !== '' ? subject.slice(0, 500) : `Email from ${displayName(target.from)}`;
}

/** The one line of notes: who sent it and when, never the body. */
export function emailNoteLine(target: Pick<EmailTaskTarget, 'from' | 'date'>): string {
  const { name, email } = target.from;
  const sender = name && name.trim() !== '' && name.trim() !== email ? `${name.trim()} <${email}>` : email;
  return `From ${sender} · ${formatFullDate(target.date)}`;
}

/** A one-tap "Remind me" choice: a due date and time, reminded at that time. */
export interface QuickRemind {
  id: 'later-today' | 'tomorrow' | 'next-week';
  label: string;
  /** When, in words: "6:00 PM", "Thu 9:00 AM". */
  when: string;
  dueDate: string;
  dueTime: string;
  remind: TaskRemind;
}

/** The morning the shortcuts ring at: the same 09:00 as a date-only reminder (ADR 0073). */
const MORNING = '09:00';
/** "Later today" rounds up to an hour at least this far ahead… */
const LATER_HOURS = 3;
/** …and is not offered when that would be after this hour. */
const LAST_LATER_HOUR = 21;

function shortWeekday(day: string): string {
  return new Intl.DateTimeFormat(dateLocale(), { timeZone: 'UTC', weekday: 'short' }).format(
    new Date(`${day}T12:00:00Z`),
  );
}

function shortDay(day: string): string {
  return new Intl.DateTimeFormat(dateLocale(), { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' }).format(
    new Date(`${day}T12:00:00Z`),
  );
}

/**
 * Gmail's snooze presets, as due + reminder: later today (three hours on,
 * rounded up to the hour; not offered after 18:00), tomorrow at 09:00, and
 * next Monday at 09:00. Days and times are wall clock in the owner's chosen
 * zone (Settings → General, else this device's) — the zone the server reads a
 * due time in, so "9:00 AM" rings at nine there (ADR 0073).
 */
export function quickReminds(now: Date = new Date()): QuickRemind[] {
  const today = todayCalendarDay(now);
  const [hours, minutes] = nowWallTime(now).split(':').map(Number) as [number, number];
  const out: QuickRemind[] = [];
  const later = hours + LATER_HOURS + (minutes > 0 ? 1 : 0);
  if (later <= LAST_LATER_HOUR) {
    const time = `${String(later).padStart(2, '0')}:00`;
    out.push({ id: 'later-today', label: 'Later today', when: formatDueTime(time), dueDate: today, dueTime: time, remind: 'at_time' });
  }
  const tomorrow = addDays(today, 1);
  out.push({
    id: 'tomorrow',
    label: 'Tomorrow morning',
    when: `${shortWeekday(tomorrow)} ${formatDueTime(MORNING)}`,
    dueDate: tomorrow,
    dueTime: MORNING,
    remind: 'at_time',
  });
  // Next Monday; on a Monday, the one after.
  const weekday = new Date(`${today}T12:00:00Z`).getUTCDay();
  const monday = addDays(today, (8 - weekday) % 7 || 7);
  out.push({
    id: 'next-week',
    label: 'Next week',
    when: `${shortDay(monday)} ${formatDueTime(MORNING)}`,
    dueDate: monday,
    dueTime: MORNING,
    remind: 'at_time',
  });
  return out;
}

/* -------------------------------------------- the list, remembered per mailbox */

const LIST_KEY = 'ols-mail.email-task-list';

function readRemembered(): Record<string, string> {
  try {
    const raw = window.localStorage.getItem(LIST_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed !== null && typeof parsed === 'object' ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

/** The list last used for an email of this mailbox, on this device. */
export function rememberedTaskList(accountId: string): string | undefined {
  const value = readRemembered()[accountId];
  return typeof value === 'string' ? value : undefined;
}

export function rememberTaskList(accountId: string, listId: string): void {
  try {
    window.localStorage.setItem(LIST_KEY, JSON.stringify({ ...readRemembered(), [accountId]: listId }));
  } catch {
    // Private mode or a full quota: the default list next time, nothing worse.
  }
}
